/**
 * CourseInstructorsPage
 *
 * Tenant-wide directory of instructor templates — the same records
 * surfaced through the per-course CourseAuthorAutocomplete, just lifted
 * to a top-level Settings surface so admins can manage the roster in
 * one place (add, edit, delete) instead of only inline on a course.
 *
 * Templates are the reusable bio + portrait + credentials that get
 * auto-filled into every course an instructor teaches. Backed by the
 * account-wide author directory in the lookups slice
 * (`features/lookups/lookupsSlice.ts`, over `CourseAuthorsClient`).
 */

import { useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '../components/ui';
import { EmptyState, PageHeader } from '../components/common';
import {
  AccountCircleIcon,
  AddIcon,
  DeleteIcon,
  EditIcon,
  SearchIcon,
} from '../components/icons';
import InstructorTemplateDialog from '../components/courses/InstructorTemplateDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import { useSelector } from 'react-redux';
import { CourseAuthor, SaveCourseAuthorRequest } from '../apis/api-client';
import { useAppDispatch } from '../stores';
import {
  createAuthor,
  deleteAuthor,
  selectAllAuthors,
  updateAuthor,
  useEnsureAuthors,
} from '../features/lookups/lookupsSlice';
import { getPortraitObjectPosition } from '../utils/courseAuthor';
import { alert } from '../hooks/function';

export default function CourseInstructorsPage() {
  const { enqueueSnackbar } = useSnackbar();

  // Shared, account-wide directory (fetched once). Mutations below dispatch
  // through the slice, which re-renders this page.
  const dispatch = useAppDispatch();
  useEnsureAuthors();
  const authors = useSelector(selectAllAuthors);

  // Type-to-filter — name, credentials, and the bio's first sentence
  // are all matched against, so admins can find someone by "HIPAA" if
  // that's the keyword they remember from the bio.
  const [query, setQuery] = useState('');
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return authors;
    return authors.filter((a) => {
      const hay = [a.name, a.credentials, a.bio].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [authors, query]);

  // Dialog state. `editingAuthor` null + open = "add new"; non-null = "edit".
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAuthor, setEditingAuthor] = useState<CourseAuthor | null>(null);

  // Confirm-delete is its own modal so a misclick on the row's delete
  // icon doesn't quietly drop an instructor that's wired into N courses.
  const [confirmDelete, setConfirmDelete] = useState<CourseAuthor | null>(null);

  const openAdd = () => {
    setEditingAuthor(null);
    setDialogOpen(true);
  };
  const openEdit = (author: CourseAuthor) => {
    setEditingAuthor(author);
    setDialogOpen(true);
  };
  const closeDialog = () => setDialogOpen(false);

  const handleSave = async (patch: SaveCourseAuthorRequest & { name: string }) => {
    try {
      if (editingAuthor?.id != null) {
        await dispatch(updateAuthor({ id: editingAuthor.id, request: patch })).unwrap();
        alert(enqueueSnackbar, 'Instructor updated', 'success');
      } else {
        await dispatch(createAuthor(patch)).unwrap();
        alert(enqueueSnackbar, 'Instructor added', 'success');
      }
      closeDialog();
    } catch (e: any) {
      alert(enqueueSnackbar, e?.message || 'Failed to save instructor', 'error');
    }
  };

  // Delete strategy: drop the row from the directory. Courses still
  // pointing at this authorId fall back to "no author" on the next read.
  const handleDelete = async (author: CourseAuthor) => {
    if (author.id == null) return;
    try {
      await dispatch(deleteAuthor(author.id)).unwrap();
      setConfirmDelete(null);
      alert(enqueueSnackbar, `Removed ${author.name ?? 'instructor'}`, 'success');
    } catch {
      alert(enqueueSnackbar, 'Failed to remove instructor', 'error');
    }
  };

  return (
    <Box sx={{ p: 4 }}>
      <PageHeader
        icon={<AccountCircleIcon />}
        title="Course Instructors"
        subtitle="Reusable instructor templates — manage the bios that fill in across every course."
      />

      {/* Toolbar — search on the left, Add on the right. The button uses
          the brand gradient so admins see this as the page's primary
          action, matching the Create CTA on Manage Content. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
          mb: 3,
        }}
      >
        <TextField
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search instructors by name, credentials, or bio"
          size="small"
          InputProps={{
            startAdornment: (
              <SearchIcon sx={{ color: 'text.disabled', mr: 1, fontSize: 20 }} />
            ),
          }}
          sx={{ flex: 1, minWidth: 280 }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openAdd}
          sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
        >
          Add instructor
        </Button>
      </Box>

      {/* Roster */}
      {authors.length === 0 ? (
        <EmptyState
          icon={<AccountCircleIcon sx={{ fontSize: 100, color: 'grey.400' }} />}
          title="No instructors yet"
          subtitle="Click Add instructor to create your first reusable template."
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<SearchIcon sx={{ fontSize: 100, color: 'grey.400' }} />}
          title={`No instructors match "${query.trim()}"`}
          subtitle="Try a different name, credential, or biography keyword."
        />
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: 2,
          }}
        >
          {filtered.map((author) => (
            <Card
              key={author.id}
              sx={{
                p: 2.5,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                transition: 'border-color 120ms ease, box-shadow 120ms ease',
                '&:hover': {
                  borderColor: 'primary.main',
                  boxShadow: '0 4px 14px rgba(0,0,0,0.06)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar
                  src={author.portraitUrl || undefined}
                  imgProps={{
                    style: { objectPosition: getPortraitObjectPosition(author) },
                  }}
                  sx={{ width: 56, height: 56, fontSize: '1.25rem', flexShrink: 0 }}
                >
                  {author.name ? author.name.charAt(0).toUpperCase() : '?'}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight={700} noWrap>
                    {author.name}
                  </Typography>
                  {author.credentials && (
                    <Chip
                      label={author.credentials}
                      size="small"
                      sx={{
                        mt: 0.25,
                        height: 20,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        bgcolor: 'primary.50',
                        color: 'primary.main',
                        '& .MuiChip-label': { px: 1 },
                      }}
                    />
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
                  <Tooltip title="Edit instructor">
                    <IconButton size="small" onClick={() => openEdit(author)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remove instructor">
                    <IconButton
                      size="small"
                      onClick={() => setConfirmDelete(author)}
                      sx={{ color: 'error.main' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {author.bio && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    lineHeight: 1.45,
                  }}
                >
                  {author.bio}
                </Typography>
              )}
            </Card>
          ))}
        </Box>
      )}

      <InstructorTemplateDialog
        open={dialogOpen}
        onClose={closeDialog}
        author={editingAuthor}
        onSave={handleSave}
      />

      <ConfirmDialog
        open={!!confirmDelete}
        title="Remove instructor"
        content={
          confirmDelete
            ? `Remove ${confirmDelete.name} from the instructor directory? Any courses currently mapped to this instructor will fall back to "no author" until you reassign one.`
            : ''
        }
        onConfirm={() => confirmDelete && handleDelete(confirmDelete)}
        onCancel={() => setConfirmDelete(null)}
      />
    </Box>
  );
}
